import { useEffect, useRef, useState } from 'react'

/** Long enough that typing a name is one write, short enough to feel immediate. */
export const AUTOSAVE_DELAY_MS = 600

export type SaveState =
  /** Nothing has been edited yet. */
  | 'clean'
  /** Edited, waiting for the pause that triggers the write. */
  | 'pending'
  | 'saving'
  | 'saved'
  /** Held back: the draft is not valid to write yet. */
  | 'blocked'
  | 'error'

interface Options<T> {
  /** Returns why the draft cannot be written, or null when it can. */
  validate?: (draft: T) => string | null
  delayMs?: number
}

/**
 * Writes a draft a short pause after the last edit, so there is no button to
 * forget. An invalid draft holds the write rather than failing it: a field
 * cleared halfway through a rename is not a mistake yet, and the next keystroke
 * that makes it valid saves it.
 *
 * `dirty` is the caller's own comparison against what is stored, which is what
 * turns a landed write into "Saved" — the snapshot coming back is the receipt.
 */
export function useAutoSave<T>(
  draft: T,
  dirty: boolean,
  write: (draft: T) => Promise<void>,
  options: Options<T> = {},
): { state: SaveState; message: string | null } {
  const { validate, delayMs = AUTOSAVE_DELAY_MS } = options
  const [state, setState] = useState<SaveState>('clean')
  const [failure, setFailure] = useState<string | null>(null)
  const everWrote = useRef(false)

  // Held in a ref so a caller passing a fresh closure each render doesn't
  // restart the timer and postpone the write forever.
  const writeRef = useRef(write)
  writeRef.current = write

  const problem = validate?.(draft) ?? null

  useEffect(() => {
    if (!dirty) {
      setState(everWrote.current ? 'saved' : 'clean')
      return
    }
    if (problem !== null) {
      setState('blocked')
      return
    }
    setState('pending')
    const id = setTimeout(() => {
      // Offline, Firestore takes the write into its own queue and the promise
      // stays pending until it reaches the server — so the dispatch is what
      // counts as saved, and the promise only ever reports a failure.
      everWrote.current = true
      setFailure(null)
      setState('saving')
      writeRef.current(draft).catch((cause: unknown) => {
        setFailure(cause instanceof Error ? cause.message : 'Could not save that.')
        setState('error')
      })
    }, delayMs)
    return () => clearTimeout(id)
  }, [draft, dirty, problem, delayMs])

  return { state, message: problem ?? failure }
}
